import type { Borders, Worksheet } from 'exceljs'

import { BOOKS_TIME_ZONE, booksToday } from '../report-range'
import { loadExcelJs } from './excel-sheet'

const XLSX_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

/**
 * A report sheet built in the browser, laid out exactly as the server lays out
 * the sales reports (`merchant-report.excel.ts` → `writeSheet`): the business
 * and the report's name, the period, outlets and generated lines, a frozen and
 * filterable navy column row, typed cells, a total row and the footnotes. The
 * inventory reports are built here because their rows come from another
 * service; a merchant opening both should not be able to tell which is which.
 */

export type ExcelColumnType =
  | 'text'
  | 'money'
  /** A cost per unit — four places, because a fork costs $0.0045. */
  | 'cost'
  | 'int'
  | 'qty'
  /** A 0–1 ratio. */
  | 'percent'
  /** An ISO instant, printed in the books' clock. */
  | 'datetime'
  /** A `YYYY-MM-DD` calendar day. */
  | 'date'

export interface ExcelColumn {
  header: string
  type: ExcelColumnType
  width?: number
  /** Dropped from the sheet when no data row has a value — no columns of zeros. */
  optional?: boolean
}

export type ExcelValue = string | number | null | undefined

export type ExcelRowKind = 'data' | 'group' | 'subtotal' | 'total'

/** A row in column order. A plain array is a data row. */
export type ExcelRow = ExcelValue[] | { kind: ExcelRowKind; values: ExcelValue[] }

export interface ExcelReport {
  fileName: string
  /** The tab — Excel caps it at 31 characters. */
  sheetName: string
  /** The business, as the server prints `merchantName` in A1. */
  business: string
  title: string
  /** The lines under the title: what the report covers, and when it was read. */
  lines: string[]
  columns: ExcelColumn[]
  rows: ExcelRow[]
  notes?: string[]
  /** What an empty report says. */
  empty?: string
}

const FONT = 'Arial'

const FORMATS: Record<ExcelColumnType, string | undefined> = {
  text: undefined,
  money: '#,##0.00;-#,##0.00;"-"',
  cost: '#,##0.0000;-#,##0.0000;"-"',
  int: '#,##0;-#,##0;"-"',
  qty: 'General',
  percent: '0.0%;-0.0%;"-"',
  datetime: 'dd-mmm-yyyy hh:mm',
  date: 'dd-mmm-yyyy',
}

const WIDTHS: Record<ExcelColumnType, number> = {
  text: 18,
  money: 13,
  cost: 12,
  int: 10,
  qty: 10,
  percent: 10,
  datetime: 18,
  date: 13,
}

const FILL: Record<ExcelRowKind | 'header', string> = {
  header: 'FF1F3864',
  data: '',
  group: 'FFDDE4F0',
  subtotal: 'FFF2F2F2',
  total: 'FFD9E1F2',
}

/** A thin line on every edge, so each column of the table reads as its own box. */
const GRID: Partial<Borders> = {
  top: { style: 'thin', color: { argb: 'FFBFBFBF' } },
  left: { style: 'thin', color: { argb: 'FFBFBFBF' } },
  bottom: { style: 'thin', color: { argb: 'FFBFBFBF' } },
  right: { style: 'thin', color: { argb: 'FFBFBFBF' } },
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** The books' wall clock at this instant, as the parts of a date. */
function wallClockParts(at: Date): number[] {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: BOOKS_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(at)
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((entry) => entry.type === type)?.value ?? 0)

  return [part('year'), part('month'), part('day'), part('hour'), part('minute'), part('second')]
}

/**
 * The outlet's wall-clock time as an Excel date.
 *
 * Excel cells have no time zone, so the cell must hold the Phnom Penh reading
 * itself. Handing exceljs the instant would print UTC, seven hours early.
 */
function wallClock(iso: string): Date | string {
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return iso

  const [year, month, day, hour, minute, second] = wallClockParts(at)

  return new Date(Date.UTC(year, month - 1, day, hour, minute, second))
}

/** A `YYYY-MM-DD` label as a real date cell, so the column sorts and filters. */
function calendarDate(label: string): Date | string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(label)
  if (!match) return label

  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])))
}

/** `2026-10-01` → `01-Oct-2026`, as the server prints its header lines. */
export function excelDay(day: string): string {
  const [year, month, date] = day.slice(0, 10).split('-')

  return `${date}-${MONTHS[Number(month) - 1]}-${year}`
}

function cellValue(type: ExcelColumnType, value: ExcelValue): string | number | Date | null {
  if (value === null || value === undefined || value === '') return null

  switch (type) {
    case 'datetime':
      return wallClock(String(value))
    case 'date':
      return calendarDate(String(value))
    case 'text':
      return String(value)
    default: {
      const number = Number(value)
      return Number.isFinite(number) ? number : String(value)
    }
  }
}

const isBlank = (value: ExcelValue) =>
  value === null || value === undefined || value === '' || value === 0

/** `Generated: 26-Oct-2026 17:21`, and the warning when the period reaches today. */
function generatedLine(includesToday: boolean): string {
  const [year, month, day, hour, minute] = wallClockParts(new Date())
  const pad = (n: number) => String(n).padStart(2, '0')
  const generated = `${pad(day)}-${MONTHS[month - 1]}-${year} ${pad(hour)}:${pad(minute)}`

  return `Generated: ${generated}${includesToday ? ' — the period includes today, so its last day can still change' : ''}`
}

/** The server's three header lines for a report over a period of trading days. */
export function excelPeriodLines(from: string, to: string, outlets: string): string[] {
  return [
    `Period: ${excelDay(from)} to ${excelDay(to)} (${BOOKS_TIME_ZONE})`,
    `Outlets: ${outlets}`,
    generatedLine(to >= booksToday()),
  ]
}

/** The header lines for a report that is a moment, not a period — stock on hand, costing. */
export function excelMomentLines(moment: string, outlets: string | null): string[] {
  return [moment, ...(outlets === null ? [] : [`Outlets: ${outlets}`]), generatedLine(false)]
}

/** `Stock-Movements_2026-10-01_to_2026-10-26.xlsx`, as the server names its files. */
export function excelReportFileName(report: string, from: string, to?: string): string {
  return to ? `${report}_${from}_to_${to}.xlsx` : `${report}_${from}.xlsx`
}

function rowOf(row: ExcelRow): { kind: ExcelRowKind; values: ExcelValue[] } {
  return Array.isArray(row) ? { kind: 'data', values: row } : row
}

function writeSheet(sheet: Worksheet, report: ExcelReport) {
  const rows = report.rows.map(rowOf)
  const dataRows = rows.filter((row) => row.kind === 'data')
  const kept = report.columns
    .map((column, index) => ({ column, index }))
    .filter(
      ({ column, index }) =>
        !column.optional || dataRows.some((row) => !isBlank(row.values[index])),
    )
  const columns = kept.map(({ column }) => column)
  const empty = report.empty ?? 'Nothing in this period'

  sheet.columns = columns.map((column) => ({ width: column.width ?? WIDTHS[column.type] }))

  sheet.addRow([report.business]).font = { name: FONT, size: 14, bold: true }
  sheet.addRow([report.title]).font = {
    name: FONT,
    size: 12,
    bold: true,
    color: { argb: 'FF1F3864' },
  }

  for (const line of report.lines) {
    sheet.addRow([line]).font = { name: FONT, size: 9, color: { argb: 'FF595959' } }
  }

  sheet.addRow([])

  const headerRow = sheet.addRow(columns.map((column) => column.header))
  headerRow.height = 30
  headerRow.eachCell((cell) => {
    cell.font = { name: FONT, size: 10, bold: true, color: { argb: 'FFFFFFFF' } }
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: FILL.header } }
    cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true }
    cell.border = GRID
  })

  sheet.views = [{ state: 'frozen', ySplit: headerRow.number, showGridLines: false }]

  const firstDataRow = headerRow.number + 1

  for (const row of rows) {
    const excelRow = sheet.addRow(
      kept.map(({ column, index }) => cellValue(column.type, row.values[index])),
    )

    excelRow.font = { name: FONT, size: 10, bold: row.kind !== 'data' }

    columns.forEach((column, index) => {
      const cell = excelRow.getCell(index + 1)
      const format = FORMATS[column.type]

      if (format) cell.numFmt = format
      cell.border =
        row.kind === 'total' ? { ...GRID, top: { style: 'double', color: { argb: 'FF808080' } } } : GRID
      if (row.kind !== 'data') {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: FILL[row.kind] } }
      }
    })
  }

  if (rows.length) {
    sheet.autoFilter = {
      from: { row: headerRow.number, column: 1 },
      to: { row: firstDataRow + rows.length - 1, column: columns.length },
    }
  } else {
    sheet.addRow([`${empty}.`]).font = { name: FONT, size: 10, italic: true }
  }

  const notes = report.notes ?? []
  if (notes.length) sheet.addRow([])

  for (const note of notes) {
    sheet.addRow([note]).font = { name: FONT, size: 9, italic: true, color: { argb: 'FF595959' } }
  }
}

export async function reportToWorkbook(report: ExcelReport): Promise<Blob> {
  const ExcelJS = await loadExcelJs()
  const workbook = new ExcelJS.Workbook()
  const sheet = workbook.addWorksheet(report.sheetName.replace(/[[\]:*?/\\]/g, '').slice(0, 31), {
    pageSetup: {
      orientation: 'landscape',
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      paperSize: 9,
    },
    views: [{ showGridLines: false }],
  })

  writeSheet(sheet, report)

  const buffer = await workbook.xlsx.writeBuffer()
  return new Blob([buffer], { type: XLSX_TYPE })
}
